import { type PerspectiveCamera, type Sphere, Ray, Spherical, Vector3 } from '@seddi/three';

export type FrameBounds = { normal: Vector3; sphere: Sphere };

export type FrameLookAt = { distance: number; origin: Vector3; pivot: Vector3 };

const normalizeAzimuth = (a: number): number => {
  if (a < -Math.PI) a += 2 * Math.PI;
  if (a > Math.PI) a -= 2 * Math.PI;
  return a;
};

export const create = () => {
  return { distance: 0, origin: new Vector3(), pivot: new Vector3() };
};

export const createFromBounds = (() => {
  const RADIUS_MARGIN = 0.075;
  const RADIUS_MIN = 0.2;

  return (
    camera: PerspectiveCamera,
    bounds: Readonly<FrameBounds>,
    frame: FrameLookAt = create(),
  ): FrameLookAt => {
    const { aspect, fov } = camera;
    let angle = (fov * Math.PI) / 360;
    if (aspect < 1) angle *= aspect;

    const radius = Math.max(RADIUS_MIN, bounds.sphere.radius + RADIUS_MARGIN);
    const distance = radius / Math.tan(angle);
    const pivot = bounds.sphere.center;
    frame.distance = distance;
    frame.origin.copy(bounds.normal).multiplyScalar(distance).add(pivot);
    frame.pivot.copy(pivot);
    return frame;
  };
})();

export const animateCamera = (() => {
  const dstOffset = new Vector3();
  const dstSpherical = new Spherical();
  const offset = new Vector3();
  const pivot = new Vector3();
  const pivotDelta = new Vector3();
  const ray = new Ray();
  const spherical = new Spherical();

  const projectPoint = (() => {
    const ap = new Vector3();
    return (ray: Ray, p: Vector3, target: Vector3) => {
      const a = ray.origin;
      const ab = ray.direction;
      ap.copy(p).sub(a);
      target.copy(ab).multiplyScalar(ap.dot(ab)).add(a);
    };
  })();

  return (
    camera: PerspectiveCamera,
    dst: Readonly<FrameLookAt>,
    moveSpeed: number,
    rotateSpeed: number,
  ): boolean => {
    const { distance: dstDistance, origin: dstOrigin, pivot: dstPivot } = dst;

    camera.getWorldDirection(ray.direction);
    camera.getWorldPosition(ray.origin);
    const origin = ray.origin;
    projectPoint(ray, dstPivot, pivot);

    pivotDelta.copy(dstPivot).sub(pivot).normalize();
    pivot.add(pivotDelta.multiplyScalar(moveSpeed));
    if (pivot.distanceTo(dstPivot) < moveSpeed * 1.1) pivot.copy(dstPivot);

    offset.copy(origin).sub(pivot);
    dstOffset.copy(dstOrigin).sub(dstPivot);
    spherical.setFromVector3(offset);
    dstSpherical.setFromVector3(dstOffset);

    const phiDiff = dstSpherical.phi - spherical.phi;
    if (Math.abs(phiDiff) < rotateSpeed * 1.1) {
      spherical.phi = dstSpherical.phi;
    } else {
      spherical.phi += phiDiff > 0 ? rotateSpeed : -rotateSpeed;
    }

    const POLE_THRESHOLD = 0.01;
    const atPole = dstSpherical.phi < POLE_THRESHOLD || dstSpherical.phi > Math.PI - POLE_THRESHOLD;
    if (atPole) {
      spherical.theta = dstSpherical.theta;
    } else {
      const thetaDiff = normalizeAzimuth(dstSpherical.theta - spherical.theta);
      if (Math.abs(thetaDiff) < rotateSpeed * 1.1) {
        spherical.theta = dstSpherical.theta;
      } else {
        spherical.theta += thetaDiff > 0 ? rotateSpeed : -rotateSpeed;
      }
    }

    let distance = pivot.distanceTo(origin);
    const distDiff = dstDistance - distance;
    if (Math.abs(distDiff) < moveSpeed * 1.1) {
      distance = dstDistance;
    } else {
      distance += distDiff > 0 ? moveSpeed : -moveSpeed;
    }

    spherical.radius = distance;
    offset.setFromSpherical(spherical);
    origin.copy(pivot).add(offset);
    if (origin.distanceTo(dstOrigin) < moveSpeed * 1.1) origin.copy(dstOrigin);

    camera.position.copy(origin);
    camera.lookAt(pivot);

    const DONE_EPSILON = 1e-5;
    return origin.distanceTo(dstOrigin) < DONE_EPSILON && pivot.distanceTo(dstPivot) < DONE_EPSILON;
  };
})();
